import { useLayoutEffect, useState } from 'react'

/**
 * What the desktop navbar can keep on one line (William 13 Oct 2026): "Our Services" / "Help & FAQ" stay
 * whenever there is room, otherwise they go to the site's burger menu; and should the bar still overflow
 * with that burger, the username next to the avatar folds too (the kit's own fallback when room runs out).
 *
 * Measured, not a breakpoint: the room depends on the login state, the language, the username and the
 * organizer shell's narrower nav. Each measure lifts our own folds (`data-folded`) and hides the burger,
 * reads the overflow of that "everything shown" layout, then puts everything back — synchronously, so
 * nothing is painted in between, and the answer does not depend on the current state (no flip-flopping).
 * Breakpoint classes (e.g. the username's `max-wide:hidden`) keep applying while measuring.
 */
export default function useNavLinksFit({ enabled, barRef, linksRef, burgerRef, nameRef, actionsRef }) {
    const [fit, setFit] = useState({ links: true, name: true })

    useLayoutEffect(() => {
        const bar = barRef.current
        const links = linksRef.current
        // `enabled` re-runs the effect when the desktop bar mounts (it is absent in the mobile layout)
        if (!enabled || !bar || !links) return undefined

        const measure = () => {
            const burger = burgerRef.current
            const name = nameRef.current
            const gap = parseFloat(getComputedStyle(bar).columnGap) || 0
            const burgerSpace = (burger?.offsetWidth || 38) + gap

            const folded = [links, name].filter((element) => element?.hasAttribute('data-folded'))
            folded.forEach((element) => element.removeAttribute('data-folded'))
            const burgerDisplay = burger?.style.display
            if (burger) burger.style.display = 'none'

            const overflow = bar.scrollWidth - bar.clientWidth
            const linksSpace = links.offsetWidth + (parseFloat(getComputedStyle(links).marginLeft) || 0) + gap

            folded.forEach((element) => element.setAttribute('data-folded', ''))
            if (burger) burger.style.display = burgerDisplay

            const linksFit = overflow <= 0
            const next = { links: linksFit, name: linksFit || overflow - linksSpace + burgerSpace <= 0 }
            setFit((current) => (current.links === next.links && current.name === next.name ? current : next))
        }

        measure()
        if (typeof ResizeObserver === 'undefined') return undefined

        let frame = 0
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(measure)
        })
        // the bar follows the window; the links and the actions change with the language and the username
        ;[bar, links, actionsRef.current].forEach((element) => element && observer.observe(element))
        return () => {
            cancelAnimationFrame(frame)
            observer.disconnect()
        }
    }, [enabled, barRef, linksRef, burgerRef, nameRef, actionsRef])

    return fit
}
